import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Calendar, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Post {
  id: number;
  title: string;
  slug: string;
  meta_description: string;
  category: string;
  created_at: string;
  views: number;
  cover_image_url?: string;
}

interface FeaturedPostsProps {
  posts: Post[];
}

export function FeaturedPosts({ posts }: FeaturedPostsProps) {
  if (posts.length === 0) return null;

  return (
    <section className="mb-16" aria-labelledby="featured-posts-heading">
      <div className="flex items-center gap-2 mb-6 animate-fade-in-up">
        <Sparkles className="w-6 h-6 text-kopf-accent" aria-hidden="true" />
        <h2 id="featured-posts-heading" className="text-2xl sm:text-3xl font-bold text-kopf-primary">Posts em Destaque</h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {posts.map((post, index) => (
          <Link 
            key={post.id} 
            to={`/post/${post.slug}`}
            className="animate-fade-in-up"
            style={{ animationDelay: `${index * 150}ms` }}
          >
            <Card className="group overflow-hidden border-kopf-secondary hover:border-kopf-accent transition-all duration-300 hover:shadow-xl h-full focus-within:ring-2 focus-within:ring-kopf-accent focus-within:ring-offset-2">
              <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden" role="img" aria-label={`Imagem do post: ${post.title}`}>
                {post.cover_image_url ? (
                  <img 
                    src={post.cover_image_url} 
                    alt={`${post.title} - Artigo sobre ${post.category} no KopfAI Blog`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading={index === 0 ? "eager" : "lazy"}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    decoding="async"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-kopf-primary to-kopf-accent" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-kopf-text/60 to-transparent" />
                <Badge className="absolute top-4 left-4 bg-kopf-accent text-white border-0 transition-transform group-hover:scale-105" aria-label="Post em destaque">
                  <Sparkles className="w-3 h-3 mr-1" aria-hidden="true" />
                  Em Destaque
                </Badge>
                <Badge className="absolute top-4 right-4 bg-kopf-primary text-white border-0 transition-transform group-hover:scale-105" aria-label={`Categoria: ${post.category}`}>
                  {post.category}
                </Badge>
              </div>
              
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-kopf-primary mb-2 line-clamp-2 group-hover:text-kopf-accent transition-colors">
                  {post.title}
                </h3>
                <p className="text-kopf-text/70 mb-4 line-clamp-2">
                  {post.meta_description}
                </p>
                
                <div className="flex items-center gap-4 text-sm text-kopf-text/60">
                  <div className="flex items-center gap-1" aria-label={`Publicado em ${format(new Date(post.created_at), 'dd MMM yyyy', { locale: ptBR })}`}>
                    <Calendar className="w-4 h-4" aria-hidden="true" />
                    <span>{format(new Date(post.created_at), 'dd MMM yyyy', { locale: ptBR })}</span>
                  </div>
                  <div className="flex items-center gap-1" aria-label={`${post.views} visualizações`}>
                    <Eye className="w-4 h-4" aria-hidden="true" />
                    <span>{post.views}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}
